<template>
  <div class="campaign-page">
    <header class="campaign-header">
      <div>
        <h1>Campaigns</h1>
        <p>Manage promotion banners and pop-ups.</p>
      </div>
      <router-link class="btn btn-primary" :to="{ name: 'campaign.create' }">
        <i class="fa fa-plus" />
        Create campaign
      </router-link>
    </header>
    <form class="campaign-panel campaign-filters" @submit.prevent="applyFilters">
      <div>
        <label for="campaign-status">Status</label>
        <select id="campaign-status" v-model="filters.enabled" class="form-control">
          <option value="">All statuses</option>
          <option value="1">Enabled</option>
          <option value="0">Disabled</option>
        </select>
      </div>
      <div>
        <label for="campaign-code-filter">Promotion ID</label>
        <input
          id="campaign-code-filter"
          v-model="filters.promotion_code_id"
          type="number"
          min="1"
          step="1"
          class="form-control"
          placeholder="All promotion codes"
        />
      </div>
      <div>
        <label for="campaign-page-size">Per page</label>
        <select
          id="campaign-page-size"
          v-model.number="filters.per_page"
          class="form-control"
        >
          <option :value="15">15</option>
          <option :value="30">30</option>
          <option :value="100">100</option>
        </select>
      </div>
      <button class="btn btn-primary" :disabled="loading">Apply filters</button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="loading"
        @click="resetFilters"
      >
        Reset
      </button>
    </form>
    <div v-if="errors.length" class="alert alert-danger" role="alert">
      <div v-for="(error, i) in errors" :key="i">{{ error }}</div>
      <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click="loadData">
        Retry
      </button>
    </div>
    <div class="campaign-panel">
      <p class="p-3 mb-0">
        Higher priority wins; the newest campaign wins ties. Enabled campaigns still
        require an eligible promotion code and an enabled placement.
      </p>
      <loading v-if="loading" />
      <div v-else-if="!errors.length" class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Promotion code</th>
              <th>Placements</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="campaign in campaigns" :key="campaign.id">
              <td>
                <router-link :to="editRoute(campaign)">
                  {{ campaign.title || 'Untitled campaign' }}
                </router-link>
                <small class="d-block">#{{ campaign.id }}</small>
              </td>
              <td>
                <router-link
                  :to="{
                    name: 'promotions.show',
                    params: { id: campaign.promotion_code_id },
                  }"
                >
                  {{
                    campaign.promotion_code
                      ? campaign.promotion_code.code
                      : '#' + campaign.promotion_code_id
                  }}
                </router-link>
              </td>
              <td>
                {{ placements(campaign) }}
                <small v-if="campaign.show_popup" class="d-block">
                  After {{ campaign.popup_delay_seconds }}s · Once per session
                </small>
              </td>
              <td>{{ campaign.priority }}</td>
              <td>
                <span
                  class="badge"
                  :class="campaign.enabled ? 'badge-success' : 'badge-secondary'"
                >
                  {{ campaign.enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </td>
              <td>
                <div class="campaign-actions">
                  <router-link
                    class="btn btn-sm btn-outline-primary"
                    :to="editRoute(campaign)"
                  >
                    Edit
                  </router-link>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="!!busy[campaign.id]"
                    @click="toggle(campaign)"
                  >
                    {{
                      busy[campaign.id]
                        ? 'Saving…'
                        : campaign.enabled
                        ? 'Disable'
                        : 'Enable'
                    }}
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    :disabled="!!busy[campaign.id]"
                    @click="deleteTarget = campaign"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!campaigns.length">
              <td colspan="6" class="text-center p-5">No campaigns found.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="campaign-pagination">
        <span>{{ total }} campaigns</span>
        <b-pagination
          :value="page"
          :total-rows="total"
          :per-page="perPage"
          :disabled="loading"
          class="mb-0"
          @input="changePage"
        />
      </div>
    </div>
    <b-modal
      :visible="!!deleteTarget"
      title="Delete campaign"
      :no-close-on-backdrop="deleting"
      :no-close-on-esc="deleting"
      :hide-header-close="deleting"
      @hidden="deleteTarget = null"
    >
      <p v-if="deleteTarget">
        Delete “{{ deleteTarget.title }}”? This also removes its translations. The
        promotion code will remain.
      </p>
      <div v-if="deleteErrors.length" class="alert alert-danger" role="alert">
        <div v-for="(error, i) in deleteErrors" :key="i">{{ error }}</div>
      </div>
      <template #modal-footer>
        <button
          class="btn btn-secondary"
          :disabled="deleting"
          @click="deleteTarget = null"
        >
          Cancel
        </button>
        <button class="btn btn-danger" :disabled="deleting" @click="remove">
          {{ deleting ? 'Deleting…' : 'Delete campaign' }}
        </button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { axios, campaignUrl, errorsFrom } from './api';
const filters = () => ({ enabled: '', promotion_code_id: '', per_page: 15 });
export default {
  name: 'CampaignList',
  data: () => ({
    campaigns: [],
    filters: filters(),
    appliedFilters: filters(),
    loading: false,
    errors: [],
    page: 1,
    total: 0,
    perPage: 15,
    busy: {},
    deleteTarget: null,
    deleting: false,
    deleteErrors: [],
    requestId: 0,
  }),
  mounted() {
    this.loadData();
  },
  watch: {
    deleteTarget() {
      this.deleteErrors = [];
    },
  },
  methods: {
    editRoute(campaign) {
      return { name: 'campaign.edit', params: { id: campaign.id } };
    },
    placements(c) {
      return (
        [c.show_banner ? 'Banner' : '', c.show_popup ? 'Pop-up' : '']
          .filter(Boolean)
          .join(', ') || 'None'
      );
    },
    applyFilters() {
      this.appliedFilters = { ...this.filters };
      this.page = 1;
      this.loadData();
    },
    resetFilters() {
      this.filters = filters();
      this.applyFilters();
    },
    changePage(page) {
      if (page !== this.page) {
        this.page = page;
        this.loadData();
      }
    },
    async loadData() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.errors = [];
      const params = {
        page: this.page,
        language: 'en',
        per_page: this.appliedFilters.per_page,
      };
      ['enabled', 'promotion_code_id'].forEach((key) => {
        if (this.appliedFilters[key] !== '') params[key] = this.appliedFilters[key];
      });
      try {
        const { data } = await axios.get(campaignUrl(), { params });
        if (requestId !== this.requestId) return;
        this.campaigns = data.data;
        this.total = data.meta.total;
        this.perPage = data.meta.per_page;
      } catch (error) {
        if (requestId === this.requestId) this.errors = errorsFrom(error);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async toggle(campaign) {
      if (this.busy[campaign.id]) return;
      this.$set(this.busy, campaign.id, true);
      this.errors = [];
      try {
        await axios.patch(campaignUrl() + '/' + campaign.id, {
          enabled: !campaign.enabled,
        });
        await this.loadData();
      } catch (error) {
        this.errors = errorsFrom(error);
      } finally {
        this.$delete(this.busy, campaign.id);
      }
    },
    async remove() {
      if (this.deleting || !this.deleteTarget) return;
      this.deleting = true;
      this.deleteErrors = [];
      try {
        await axios.delete(campaignUrl() + '/' + this.deleteTarget.id);
        this.deleteTarget = null;
        if (this.campaigns.length === 1 && this.page > 1) this.page--;
        await this.loadData();
      } catch (error) {
        this.deleteErrors = errorsFrom(error);
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
<style src="./campaigns.css"></style>
